import React, { useState, useEffect } from 'react';

import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';

import Layout from '../../../containers/Layout';
import FormContainer from '../../../components/AddOrEditPage/FormContainer';
import AddOrEditPageTabs from '../../../components/AddOrEditPage/AddOrEditPageTabs';
import Alert from '../../../components/Alert';
import ParseurResultList from '../../../components/ParseurResultList';
import Loader from '../../../components/Loader';
import ImportDocumentButton from '../../../components/Imports/ImportDocumentButton';

import ImportDocumentService from '../../../services/ImportDocumentService';
import useImportDocumentForm from '../../../forms/admin/importDocument';
import { useImportDocumentStore } from '../../../store';

export default function ImportDocumentPage() {
  const [areResultsReady, setAreResultsReady] = useState(false);

  const navigate = useNavigate();
  const { documentId } = useParams();

  const setSelectedBorrower = useImportDocumentStore(
    (state) => state.setSelectedBorrower
  );
  const setSelectedCoborrower = useImportDocumentStore(
    (state) => state.setSelectedCoborrower
  );
  const setImportDocumentChanges = useImportDocumentStore(
    (state) => state.setImportDocumentChanges
  );

  // Reset the selected borrower, coborrower and import document changes when the component mounts
  useEffect(() => {
    return () => {
      setSelectedBorrower(null);
      setSelectedCoborrower(null);
      setImportDocumentChanges([]);
    };
  }, [setImportDocumentChanges, setSelectedBorrower, setSelectedCoborrower]);

  const { data: importDocument, refetch } = useQuery({
    queryKey: ['importDocument', documentId],
    queryFn: () => ImportDocumentService.getImportDocument(documentId),
    enabled: !!documentId,
    refetchInterval: areResultsReady ? false : 10000,
  });

  useEffect(() => {
    if (
      importDocument?._document?.parseur_result &&
      Object.keys(importDocument._document.parseur_result).length > 0
    ) {
      setAreResultsReady(true);
    }
  }, [importDocument]);

  const tabs = [
    {
      label: 'Loans',
      onClick: () => {
        navigate('/loans');
      },
    },
    {
      label: 'Imports',
      onClick: () => {
        navigate('/loans/imports');
      },
      active: true,
    },
  ];

  const form = useImportDocumentForm(() => {}, importDocument);

  let alert = '';

  if (
    !importDocument?._document?.parseur_mailbox_description &&
    importDocument?.status === 'REVIEW'
  ) {
    alert =
      'There was a problem importing this document. Please contact support.';
  } else if (
    !importDocument?._document?.parseur_mailbox_description &&
    importDocument?.status === 'PENDING'
  ) {
    alert = 'This document is still being analyzed.';
  }

  return (
    <Layout>
      <div className="flex min-h-[calc(100dvh-4rem)]">
        {/* Content area */}
        <div className="relative flex flex-col flex-1 overflow-y-auto overflow-x-hidden">
          <main className="grow">
            <div className="px-4 sm:px-6 lg:px-8 py-8 pb-10 w-full max-w-9xl mx-auto">
              {/* Page header */}
              <div className="sm:flex sm:justify-between sm:items-center mb-8">
                {/* Left: Title */}
                <div className="mb-4 sm:mb-0 flex justify-between w-full flex-col sm:flex-row gap-2">
                  <h1 className="text-2xl md:text-3xl text-slate-800 dark:text-slate-100 font-bold">
                    Import Document Details
                  </h1>
                  <div className="grid grid-flow-col sm:auto-cols-max justify-start sm:justify-end gap-2">
                    <ImportDocumentButton
                      importDocument={importDocument}
                      refetch={refetch}
                    />
                  </div>
                </div>
              </div>

              <div className="mb-2 sm:w-60 relative z-0">
                <AddOrEditPageTabs tabs={tabs} />
              </div>

              <div className="p-7 col-span-full sm:col-span-6 xl:col-span-4 bg-white dark:bg-slate-800 shadow-lg rounded-sm border border-slate-200 dark:border-slate-700 mb-10">
                <div className="mb-10">
                  <FormContainer
                    groupSizeOffset={10}
                    isVisible={false}
                    form={form}
                    noBillComId
                    noTitle
                    noSubmit
                  />
                </div>
                {alert && (
                  <div className="mb-5">
                    <Alert message={alert} />
                  </div>
                )}
                {areResultsReady ? (
                  <ParseurResultList
                    result={importDocument?._document?.parseur_result}
                    document={importDocument?._document}
                    refetch={refetch}
                    lenderId={importDocument?._import?.lender_id}
                  />
                ) : (
                  <div className="flex items-center justify-center flex-col gap-3">
                    <span>Extracting document</span>
                    <Loader />
                  </div>
                )}
              </div>
            </div>
          </main>
        </div>
      </div>
    </Layout>
  );
}
